import { clearPersistedSmartFormChat } from './smartFormChatCache';
import { fetchFormDefinition, invalidateFormDefinitionCache } from './workflow';
import { Model } from 'survey-core';
export const FORM_SESSION_TTL_MS = 60 * 60 * 1000;
const formSessions = new Map();
/** Coalesce concurrent first-loads so two callers never create two Models. */
const inFlightLoads = new Map();
function isSessionValid(session) {
    return !!session && Date.now() - session.fetchedAt < FORM_SESSION_TTL_MS;
}
/** Returns the in-memory session when still within TTL (no network). */
export function getValidFormSession(formId) {
    const session = formSessions.get(formId);
    if (!isSessionValid(session)) {
        if (session) {
            formSessions.delete(formId);
        }
        return null;
    }
    return session;
}
export function invalidateFormSession(formId, invalidateDefinition = false) {
    if (formId) {
        formSessions.delete(formId);
        inFlightLoads.delete(formId);
        clearPersistedSmartFormChat(formId);
        if (invalidateDefinition) {
            invalidateFormDefinitionCache(formId);
        }
        return;
    }
    formSessions.clear();
    inFlightLoads.clear();
    clearPersistedSmartFormChat();
    if (invalidateDefinition) {
        invalidateFormDefinitionCache();
    }
}
export async function getOrLoadFormSession(formId, options = {}) {
    const existing = getValidFormSession(formId);
    if (existing) {
        return { detail: existing.detail, survey: existing.survey, fromCache: true };
    }
    const pending = inFlightLoads.get(formId);
    if (pending) {
        return pending;
    }
    const loadPromise = (async () => {
        // Re-check after awaiting any gap — another caller may have finished.
        const raced = getValidFormSession(formId);
        if (raced) {
            return { detail: raced.detail, survey: raced.survey, fromCache: true };
        }
        const detail = await fetchFormDefinition(formId);
        const afterFetch = getValidFormSession(formId);
        if (afterFetch) {
            return { detail: afterFetch.detail, survey: afterFetch.survey, fromCache: true };
        }
        const survey = new Model(detail.survey);
        survey.widthMode = 'responsive';
        if (options.allowSubmit) {
            survey.completeText = 'Prepare for submit';
            survey.showCompleteButton = true;
        }
        else {
            survey.showCompleteButton = false;
        }
        formSessions.set(formId, {
            formId,
            detail,
            survey,
            fetchedAt: Date.now(),
        });
        return { detail, survey, fromCache: false };
    })().finally(() => {
        inFlightLoads.delete(formId);
    });
    inFlightLoads.set(formId, loadPromise);
    return loadPromise;
}
